// import components
import { GridColDef } from '@mui/x-data-grid-pro';
import { DutyCodePeriods } from 'components/shared/DataGrid/components/CustomCellRenderer/DutyCodePeriods';
import ColorCell from 'components/shared/Table/TableCell/DataGrid/ColorCell';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

// import hooks
import { useMemo } from 'react';
import { useGetOptionsForCLA } from 'hooks/useGetOptionsForCLA';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'hooks';

// import types
import { DutyCode, DutyCodePeriod } from 'generated/index';

// import utils
import { booleanValueGetterByField, dateFormatter } from 'utils/grid';
import { dateSortComparator } from 'components/roster-portal/activity-planning/components/ClientAbsencePeriodsDialog/utils/clientAbsencePeriodsDialog.utils';

export const useGetColumnDefs = (): GridColDef[] => {
  const { t } = useTranslation('common');
  const { hasActivityPlanningViewAccess } = useSelector(({ grants }) => grants);

  const options = useGetOptionsForCLA();

  return useMemo(
    () => [
      {
        field: 'dutyCode',
        headerName: t('dutyCode'),
        hideable: false,
        width: 90
      },
      {
        field: 'colorCode',
        headerName: t('color'),
        renderCell: ({ value }) => <ColorCell value={value} />,
        width: 50
      },
      {
        field: 'periods',
        headerName: t('periods'),
        minWidth: 300,
        renderCell: ({ row }) => {
          // we inject here the whole row because DutyCodePeriods expects the periods to be in the row object
          return (
            <DutyCodePeriods periods={row.periods ?? []} periodId="periods" />
          );
        },
        flex: 1,
        valueGetter: (periods: DutyCodePeriod) => {
          // we return here only the description of the dutyHourType for the built-in datagrid filtering purposes
          return Array.isArray(periods)
            ? periods.map((p) => p.dutyHourType?.description || '').join(' ')
            : '';
        },
        sortComparator: (_v1, _v2, param1, param2) => {
          const a = param1.api.getRow(param1.id)?.periods[0];
          const b = param2.api.getRow(param2.id)?.periods[0];

          return dateSortComparator(a?.startTime, b?.startTime);
        }
      },
      {
        field: 'description',
        headerName: t('description'),
        type: 'string',
        flex: 1
      },
      {
        field: 'validFrom',
        headerName: t('validFrom'),
        type: 'date',
        valueFormatter: dateFormatter,
        minWidth: 100
      },
      {
        field: 'validTo',
        headerName: t('validTo'),
        type: 'date',
        valueFormatter: dateFormatter,
        minWidth: 100
      },
      {
        field: 'REQUEST_EXCLUDE',
        headerName: t('dutyCodes.flags.REQUEST_EXCLUDE'),
        type: 'boolean',
        valueGetter: (_, row: DutyCode) =>
          booleanValueGetterByField('REQUEST_EXCLUDE', row),
        renderCell: ({ value }) => <BooleanCell value={value} />,
        minWidth: 80
      },
      ...(hasActivityPlanningViewAccess
        ? ([
            {
              field: 'ROUTE',
              headerName: t('dutyCodes.flags.ROUTE'),
              type: 'boolean',
              valueGetter: (_, row: DutyCode) =>
                booleanValueGetterByField('ROUTE', row),
              renderCell: ({ value }) => <BooleanCell value={value} />,
              minWidth: 80
            }
          ] satisfies GridColDef[])
        : [])
    ],
    [t, options, hasActivityPlanningViewAccess]
  );
};
